/**
 * Photo Editor Module
 */
import Konva from "konva";
import { OVAL_RATIO, VERTICAL_RECT_RATIO, PHOTO_SHAPES } from "./constants.js";

export class PhotoEditor {
	constructor(stageManager, appState) {
		this.stageManager = stageManager;
		this.appState = appState;
		this.$photoOverlay = jQuery(".mc-photo-overlay");
		this.$photoInput = jQuery(".mc-photo-input");
	}

	/**
	 * Determine a stage size that fits within the current viewport.
	 */
	getResponsiveStageDimensions() {
		const viewportWidth =
			window.innerWidth ||
			document.documentElement.clientWidth ||
			screen.width ||
			700;
		const isSmallScreen = viewportWidth <= 575;
		const horizontalPadding = isSmallScreen ? 70 : 160;
		const minWidth = 260;
		const maxWidth = 700;
		const computedWidth = Math.max(
			minWidth,
			Math.min(maxWidth, viewportWidth - horizontalPadding)
		);

		return {
			width: computedWidth,
			height: isSmallScreen ? 300 : 520,
		};
	}

	getCropDimensions(shapeType = this.appState.cropShapeType) {
		const stage = this.appState.photoStage;
		if (!stage) {
			return { width: 200, height: 260 };
		}

		const stageW = stage.width();
		const stageH = stage.height();
		const isRect = shapeType === PHOTO_SHAPES.RECTANGLE;
		const ratio = isRect ? VERTICAL_RECT_RATIO : OVAL_RATIO;
		const baseWidth = stageW * (isRect ? 0.28 : 0.36);

		let width = baseWidth;
		let height = width * ratio;

		const maxWidth = stageW - 80;
		const maxHeight = stageH - 80;
		const widthScale = maxWidth / width;
		const heightScale = maxHeight / height;
		const scale = Math.min(1, widthScale, heightScale);

		width *= scale;
		height *= scale;

		const minWidth = isRect ? 120 : 100;
		const minHeight = isRect ? 160 : 120;

		return {
			width: Math.max(width, minWidth),
			height: Math.max(height, minHeight),
		};
	}

	buildCropShapeNode(shapeType = this.appState.cropShapeType) {
		const stage = this.appState.photoStage;
		if (!stage) return null;

		const { width, height } = this.getCropDimensions(shapeType);
		const cx = stage.width() / 2;
		const cy = stage.height() / 2;

		if (shapeType === PHOTO_SHAPES.RECTANGLE) {
			const rect = new Konva.Rect({
				x: cx,
				y: cy,
				width,
				height,
				offsetX: width / 2,
				offsetY: height / 2,
				stroke: "#4caf50",
				strokeWidth: 2,
				draggable: true,
				name: "mc-photo-frame",
				listening: true,
			});
			this.attachTransformHandler(rect, shapeType);
			return rect;
		}

		const ellipse = new Konva.Ellipse({
			x: cx,
			y: cy,
			radiusX: width / 2,
			radiusY: height / 2,
			stroke: "#4caf50",
			strokeWidth: 2,
			draggable: true,
			name: "mc-photo-frame",
			listening: true,
		});
		this.attachTransformHandler(ellipse, shapeType);
		return ellipse;
	}

	attachTransformHandler(shapeNode, shapeType) {
		if (shapeType === PHOTO_SHAPES.RECTANGLE) {
			shapeNode.on("transform", () => {
				const newWidth = Math.max(
					80,
					shapeNode.width() * shapeNode.scaleX()
				);
				const newHeight = Math.max(
					120,
					shapeNode.height() * shapeNode.scaleY()
				);
				shapeNode.width(newWidth);
				shapeNode.height(newHeight);
				shapeNode.offsetX(newWidth / 2);
				shapeNode.offsetY(newHeight / 2);
				shapeNode.scaleX(1);
				shapeNode.scaleY(1);
				this.appState.frameLayer.batchDraw();
			});
			return;
		}

		shapeNode.on("transform", () => {
			const radiusX = shapeNode.radiusX() * shapeNode.scaleX();
			const clampedRX = Math.max(30, radiusX);
			shapeNode.radiusX(clampedRX);
			shapeNode.radiusY(clampedRX * OVAL_RATIO);
			shapeNode.scaleX(1);
			shapeNode.scaleY(1);
			this.appState.frameLayer.batchDraw();
		});
	}

	createCropTransformer(shapeNode, shapeType) {
		if (shapeType === PHOTO_SHAPES.RECTANGLE) {
			return new Konva.Transformer({
				nodes: [shapeNode],
				rotateEnabled: false,
				keepRatio: true,
				rotateAnchorOffset: 20,
				enabledAnchors: [
					"top-left",
					// "top-center",
					"top-right",
					// "middle-right",
					"bottom-right",
					// "bottom-center",
					"bottom-left",
					// "middle-left",
				],
				boundBoxFunc: (oldBox, newBox) => {
					if (newBox.width < 80 || newBox.height < 120) return oldBox;
					return newBox;
				},
			});
		}

		return new Konva.Transformer({
			nodes: [shapeNode],
			rotateAnchorOffset: 20,
			enabledAnchors: ["middle-left", "middle-right"],
			boundBoxFunc: (oldBox, newBox) => {
				if (newBox.width < 60) return oldBox;
				return newBox;
			},
		});
	}

	syncCropFrame(shapeType = this.appState.cropShapeType) {
		if (!this.appState.frameLayer) return;

		if (this.appState.cropTransformer) {
			this.appState.cropTransformer.destroy();
			this.appState.cropTransformer = null;
		}

		if (this.appState.cropShapeNode) {
			this.appState.cropShapeNode.destroy();
			this.appState.cropShapeNode = null;
		}

		const shapeNode = this.buildCropShapeNode(shapeType);
		if (!shapeNode) return;

		this.appState.cropShapeNode = shapeNode;
		this.appState.frameLayer.add(shapeNode);

		this.appState.cropTransformer = this.createCropTransformer(
			shapeNode,
			shapeType
		);
		this.appState.frameLayer.add(this.appState.cropTransformer);
		this.appState.frameLayer.draw();
	}

	resetCropFrame() {
		if (!this.appState.photoStage) return;
		if (!this.appState.cropShapeNode) {
			this.syncCropFrame(this.appState.cropShapeType);
			return;
		}

		const { width, height } = this.getCropDimensions(
			this.appState.cropShapeType
		);
		const stageW = this.appState.photoStage.width();
		const stageH = this.appState.photoStage.height();

		this.appState.cropShapeNode.position({
			x: stageW / 2,
			y: stageH / 2,
		});

		if (this.appState.cropShapeType === PHOTO_SHAPES.RECTANGLE) {
			this.appState.cropShapeNode.width(width);
			this.appState.cropShapeNode.height(height);
			this.appState.cropShapeNode.offsetX(width / 2);
			this.appState.cropShapeNode.offsetY(height / 2);
		} else {
			this.appState.cropShapeNode.radiusX(width / 2);
			this.appState.cropShapeNode.radiusY(height / 2);
		}

		if (this.appState.cropTransformer) {
			this.appState.cropTransformer.nodes([this.appState.cropShapeNode]);
		}

		this.appState.frameLayer.draw();
	}

	setCropShapeType(shapeType) {
		const allowed = [PHOTO_SHAPES.OVAL, PHOTO_SHAPES.RECTANGLE];
		if (!allowed.includes(shapeType)) return;
		if (this.appState.cropShapeType === shapeType) return;

		this.appState.cropShapeType = shapeType;
		if (this.appState.photoStage) {
			this.syncCropFrame(shapeType);
		}
	}

	/**
	 * Initialize photo stage
	 */
	initPhotoStage() {
		if (this.appState.photoStage) return;

		const $cont = jQuery("#mc-photo-stage-container");
		const { width: fallbackWidth, height: fallbackHeight } =
			this.getResponsiveStageDimensions();
		const w = $cont.width() || fallbackWidth;
		const h = $cont.height() || fallbackHeight;

		this.appState.photoStage = new Konva.Stage({
			container: "mc-photo-stage-container",
			width: w,
			height: h,
		});

		this.appState.photoLayer = new Konva.Layer();
		this.appState.frameLayer = new Konva.Layer();

		this.appState.photoStage.add(this.appState.photoLayer);
		this.appState.photoStage.add(this.appState.frameLayer);

		this.syncCropFrame(this.appState.cropShapeType);
	}

	/**
	 * Open photo editor
	 */
	openPhotoEditor(src) {
		this.initPhotoStage();
		this.appState.originalPhotoSrc = src;
		this.$photoOverlay.fadeIn(150);

		const img = new window.Image();
		img.onload = () => {
			this.appState.photoLayer.destroyChildren();

			const stageW = this.appState.photoStage.width();
			const stageH = this.appState.photoStage.height();

			const scale = Math.min(stageW / img.width, stageH / img.height);
			this.appState.photoInitialScale = scale;

			const dispW = img.width * scale;
			const dispH = img.height * scale;

			this.appState.photoImageNode = new Konva.Image({
				image: img,
				x: (stageW - dispW) / 2,
				y: (stageH - dispH) / 2,
				width: dispW,
				height: dispH,
				listening: false,
			});

			this.appState.photoLayer.add(this.appState.photoImageNode);
			this.appState.photoLayer.draw();

			this.resetCropFrame();
			jQuery(".mc-photo-shape-btn").removeClass("is-active");
			jQuery(
				`.mc-photo-shape-btn[data-shape='${this.appState.cropShapeType}']`
			).addClass("is-active");

			jQuery(".mc-photo-zoom").val(1);
		};
		img.src = src;
	}

	/**
	 * Close photo editor
	 */
	closePhotoEditor() {
		this.$photoOverlay.fadeOut(150);
	}

	/**
	 * Apply photo crop and add to main stage
	 */
	applyPhotoCrop() {
		if (
			!this.appState.photoStage ||
			!this.appState.photoImageNode ||
			!this.appState.cropShapeNode
		) {
			this.closePhotoEditor();
			return;
		}

		// Get crop shape bounding rect
		const rect = this.appState.cropShapeNode.getClientRect({
			relativeTo: this.appState.photoStage,
		});

		// Crop photo layer
		const croppedDataUrl = this.appState.photoLayer.toDataURL({
			x: rect.x,
			y: rect.y,
			width: rect.width,
			height: rect.height,
			pixelRatio: 2,
		});

		// Add to main stage
		this.addPhotoToMainStage(croppedDataUrl, this.appState.cropShapeType);

		// Save photo state
		const photoId = "p" + Date.now();
		window.mcPhotoState.push({
			id: photoId,
			originalSrc: this.appState.originalPhotoSrc,
			shapeType: this.appState.cropShapeType,
			crop: {
				x: rect.x,
				y: rect.y,
				width: rect.width,
				height: rect.height,
				stageWidth: this.appState.photoStage.width(),
				stageHeight: this.appState.photoStage.height(),
			},
		});

		this.closePhotoEditor();
	}

	/**
	 * Add processed photo to main stage according to selected shape
	 */
	addPhotoToMainStage(dataUrl, shapeType = this.appState.cropShapeType) {
		const img = new window.Image();
		img.onload = () => {
			const stage = this.stageManager.getStage();
			const mainLayer = this.stageManager.getMainLayer();
			const transformer = this.stageManager.getTransformer();

			const originalW = img.width;
			const originalH = img.height;
			const originalAspectRatio = originalW / originalH;

			const targetArea = 40000;
			const sizeMultiplier = 1; // Double previous default size on canvas
			const targetH =
				Math.sqrt(targetArea / originalAspectRatio) * sizeMultiplier;
			const targetW = targetH * originalAspectRatio;

			const group = new Konva.Group({
				x: stage.width() / 2 - targetW / 2,
				y: stage.height() / 2 - targetH / 2,
				draggable: true,
				name: "mc-photo-group",
			});

			group.clipFunc((ctx) => {
				ctx.beginPath();
				if (shapeType === PHOTO_SHAPES.RECTANGLE) {
					ctx.rect(0, 0, targetW, targetH);
				} else {
					const cx = targetW / 2;
					const cy = targetH / 2;
					ctx.ellipse(
						cx,
						cy,
						targetW / 2,
						targetH / 2,
						0,
						0,
						Math.PI * 2
					);
				}
				ctx.closePath();
			});

			let backgroundNode;
			if (shapeType === PHOTO_SHAPES.RECTANGLE) {
				backgroundNode = new Konva.Rect({
					x: 0,
					y: 0,
					width: targetW,
					height: targetH,
					cornerRadius: 18,
					fill: "#111111",
					shadowColor: "#000000",
					shadowBlur: 25,
					shadowOpacity: 0.75,
				});
			} else {
				backgroundNode = new Konva.Ellipse({
					x: targetW / 2,
					y: targetH / 2,
					radiusX: targetW / 2,
					radiusY: targetH / 2,
					fill: "#111111",
					shadowColor: "#000000",
					shadowBlur: 25,
					shadowOpacity: 0.8,
				});
			}

			const photoNode = new Konva.Image({
				image: img,
				x: 0,
				y: 0,
				width: targetW,
				height: targetH,
			});

			// Apply grayscale filter
			photoNode.cache();
			photoNode.filters([Konva.Filters.Grayscale]);

			group.add(backgroundNode);
			group.add(photoNode);
			group.setAttr("photoShapeType", shapeType);
			group.setAttr("mcIsGrayscale", true);

			// Store initial state for reset
			group.setAttr("initialWidth", targetW);
			group.setAttr("initialHeight", targetH);
			group.setAttr("baseScaleX", 1);
			group.setAttr("baseScaleY", 1);
			group.setAttr("baseRotation", 0);
			group.setAttr("baseX", group.x());
			group.setAttr("baseY", group.y());

			mainLayer.add(group);
			mainLayer.draw();

			// Create photo transformer (lock oval + rectangle proportions)
			const lockAspect =
				shapeType === PHOTO_SHAPES.OVAL ||
				shapeType === PHOTO_SHAPES.RECTANGLE;
			const photoTransformer = new Konva.Transformer({
				rotateEnabled: true,
				keepRatio: lockAspect,
				rotateAnchorOffset: 20,
				enabledAnchors: [
					"top-left",
					// "top-center",
					"top-right",
					// "middle-right",
					"bottom-right",
					// "bottom-center",
					"bottom-left",
					// "middle-left",
				],
				boundBoxFunc: (oldBox, newBox) => {
					if (newBox.width < 40 || newBox.height < 40) return oldBox;
					return newBox;
				},
			});

			mainLayer.add(photoTransformer);
			photoTransformer.setAttr("mcPhotoOwnerId", group._id);
			group.setAttr("mcPhotoTransformerRef", photoTransformer._id);

			const selectGroup = (evt) => {
				const multiSelect = this.isMultiSelectGesture(evt);
				if (multiSelect) {
					photoTransformer.nodes([]);
				} else {
					transformer.nodes([]);
					photoTransformer.nodes([group]);
				}

				if (window.mcSetActiveElement) {
					window.mcSetActiveElement(group, { multiSelect });
				}

				mainLayer.draw();
			};

			const handleDragStart = () => {
				jQuery(".mc-element-toolbar[data-mode='image']").hide();
			};

			selectGroup();

			group.on("click tap", selectGroup);
			group.on("dragstart", handleDragStart);
			group.on("dragend", selectGroup);
		};
		img.src = dataUrl;
	}

	/**
	 * Toggle grayscale filter for selected photo group
	 */
	togglePhotoColorMode(photoGroup, forcedState) {
		if (!photoGroup || typeof photoGroup.hasName !== "function") {
			return null;
		}

		const isPhotoGroup = photoGroup.hasName("mc-photo-group");
		if (!isPhotoGroup) return null;

		const photoNode = photoGroup.findOne("Image");
		if (!photoNode) return null;

		const storedState = photoGroup.getAttr("mcIsGrayscale");
		const isGrayscale =
			typeof storedState === "boolean" ? storedState : true;
		const nextState =
			typeof forcedState === "boolean" ? forcedState : !isGrayscale;

		if (nextState) {
			photoNode.cache();
			photoNode.filters([Konva.Filters.Grayscale]);
		} else {
			photoNode.filters([]);
			photoNode.clearCache();
		}

		photoGroup.setAttr("mcIsGrayscale", nextState);

		const layer = photoGroup.getLayer();
		if (layer) {
			layer.batchDraw();
		}

		return nextState;
	}

	/**
	 * Zoom photo
	 */
	zoomPhoto(factor) {
		if (!this.appState.photoImageNode) return;

		const s = this.appState.photoInitialScale * factor;
		const img = this.appState.photoImageNode.image();
		if (!img) return;

		const stageW = this.appState.photoStage.width();
		const stageH = this.appState.photoStage.height();
		const dispW = img.width * s;
		const dispH = img.height * s;

		this.appState.photoImageNode.width(dispW);
		this.appState.photoImageNode.height(dispH);
		this.appState.photoImageNode.x((stageW - dispW) / 2);
		this.appState.photoImageNode.y((stageH - dispH) / 2);

		this.appState.photoLayer.batchDraw();
	}

	isMultiSelectGesture(evt) {
		const nativeEvent = evt && evt.evt ? evt.evt : evt;
		if (!nativeEvent) return false;
		return Boolean(
			nativeEvent.shiftKey || nativeEvent.metaKey || nativeEvent.ctrlKey
		);
	}
}
